import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Target, TrendingUp, Calendar, CheckCircle, 
  Flame, Trophy, ArrowRight, Sparkles, Info,
  Apple, Moon, Activity, Zap
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, differenceInDays } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface Challenge {
  id: string;
  title: string;
  description: string;
  goal: string;
  duration: number; // days
  targetMetric: string;
  targetValue: number;
  currentValue: number;
  baselineValue: number;
  expectedImprovement: string;
  scienceRationale: string;
  dailyActions: string[];
  progress: number; // 0-100
  daysCompleted: number;
  daysRemaining: number;
  startDate: string;
  endDate: string;
  status: 'active' | 'completed' | 'suggested';
  icon: any;
  colour: string;
}

interface PersonalisedChallengeProps {
  userId: string;
  onStartChallenge?: () => void;
}

export default function PersonalisedChallenge({ userId, onStartChallenge }: PersonalisedChallengeProps) {
  const [suggestedChallenge, setSuggestedChallenge] = useState<Challenge | null>(null);
  const [activeChallenge, setActiveChallenge] = useState<Challenge | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    analysePatternsAndSuggestChallenge();
    loadActiveChallenge();
  }, [userId]);

  const analysePatternsAndSuggestChallenge = async () => {
    try {
      const { data: checkins } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', userId)
        .gte('date', format(subDays(new Date(), 30), 'yyyy-MM-dd'))
        .order('date', { ascending: false });

      if (!checkins || checkins.length < 7) {
        setIsLoading(false);
        return;
      }

      const recent7 = checkins.slice(0, 7);
      
      // Calculate averages
      const avgProcessedFood = average(recent7.map(c => c.processed_food_level || 0));
      const avgSleep = average(recent7.map(c => c.sleep_quality || 0));
      const avgWholeFoods = average(recent7.map(c => c.healthy_food_level || 0));
      const avgExercise = average(recent7.map(c => c.exercise_duration || 0));
      
      const moodScore = (emotion: string | null) => {
        const map: Record<string, number> = {
          'Joy & Contentment': 10, 'Peace & Calm': 8, 'Neutral': 5,
          'Sadness & Grief': 3, 'Anxiety & Fear': 2
        };
        return map[emotion || ''] || 5;
      };
      const avgMood = average(recent7.map(c => moodScore(c.emotion)));

      // Determine most impactful challenge
      let challenge: Challenge | null = null;

      // HIGH PROCESSED FOOD → Whole Foods Challenge
      // Science: 21 days minimum for initial habit formation (Lally et al., 2009)
      // 66 days average for full automaticity, but 21-30 days shows measurable change
      if (avgProcessedFood > 6) {
        const sleepImprovement = (10 - avgSleep) * 0.3; // Expected 30% sleep improvement
        const moodImprovement = (10 - avgMood) * 0.25; // Expected 25% mood improvement
        
        challenge = {
          id: 'whole-foods-21',
          title: '21-Day Whole Foods Reset',
          description: 'Transform your health by prioritising whole, unprocessed foods for scientifically-validated habit formation',
          goal: 'Achieve an average whole foods score of 8+ daily for 21 days',
          duration: 21,
          targetMetric: 'Whole Foods Score',
          targetValue: 8,
          currentValue: avgWholeFoods,
          baselineValue: avgWholeFoods,
          expectedImprovement: `Based on your data: Sleep quality may improve by ${sleepImprovement.toFixed(1)} points, mood by ${moodImprovement.toFixed(1)} points, and energy levels by 20-30%. Research shows 21 days is the minimum for initial habit formation.`,
          scienceRationale: 'Whole foods reduce systemic inflammation by up to 29%, stabilise blood sugar preventing crashes, and provide micronutrients that support neurotransmitter production. 21 days allows initial habit formation and measurable physiological changes (Mendonça et al., 2016; Lally et al., 2009).',
          dailyActions: [
            'Eat 5+ servings of vegetables/fruits',
            'Choose whole grains over refined',
            'Include lean protein with each meal',
            'Avoid ultra-processed snacks',
            'Prep whole food snacks (nuts, fruits, veggie sticks)'
          ],
          progress: 0,
          daysCompleted: 0,
          daysRemaining: 21,
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(Date.now() + 21 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
          status: 'suggested',
          icon: Apple,
          colour: 'text-accent'
        };
      }
      // LOW SLEEP → Sleep Optimisation Challenge
      else if (avgSleep < 6) {
        const moodImprovement = (10 - avgMood) * 0.35;
        const energyImprovement = 30; // percent
        
        challenge = {
          id: 'sleep-optimisation-21',
          title: '21-Day Sleep Reset',
          description: 'Master sleep hygiene to boost recovery, mood, and performance with lasting results',
          goal: 'Achieve 7.5+ hours of quality sleep nightly for 21 consecutive days',
          duration: 21,
          targetMetric: 'Sleep Quality',
          targetValue: 8,
          currentValue: avgSleep,
          baselineValue: avgSleep,
          expectedImprovement: `Expected improvements: Mood +${moodImprovement.toFixed(1)} points, energy +${energyImprovement}%, cognitive function +15-20%. 21 days allows your circadian rhythm to fully recalibrate.`,
          scienceRationale: 'Consistent 7-9 hour sleep enhances memory consolidation by 40%, reduces inflammation markers, and optimises hormone balance. 21 days allows circadian rhythm entrainment and sustained habit formation (Walker, 2017; Lally et al., 2009).',
          dailyActions: [
            'Same bed/wake time (±30 min)',
            'No screens 90 minutes before bed',
            'Room temp 16-19°C (60-67°F)',
            'Morning sunlight within 1 hour of waking',
            'No caffeine after 2pm'
          ],
          progress: 0,
          daysCompleted: 0,
          daysRemaining: 21,
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(Date.now() + 21 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
          status: 'suggested',
          icon: Moon,
          colour: 'text-primary'
        };
      }
      // LOW EXERCISE → Movement Challenge
      else if (avgExercise < 20) {
        challenge = {
          id: 'movement-foundation-21',
          title: '21-Day Movement Foundation',
          description: 'Build sustainable exercise habits starting with 20 minutes daily for lasting change',
          goal: 'Complete 20+ minutes of movement daily for 21 days',
          duration: 21,
          targetMetric: 'Exercise Minutes',
          targetValue: 20,
          currentValue: avgExercise,
          baselineValue: avgExercise,
          expectedImprovement: 'Expected: Energy +25%, mood +20%, sleep quality +15%, reduced stress by 30%. 21 days creates sustainable movement habits.',
          scienceRationale: 'Just 20 minutes of moderate exercise releases endorphins, increases BDNF (brain fertiliser), improves insulin sensitivity, and enhances mitochondrial function. 21 days establishes neural pathways for habit automaticity (Pedersen & Saltin, 2015; Lally et al., 2009).',
          dailyActions: [
            '20 min walk, jog, or swim',
            'Bodyweight exercises (10 min routine)',
            'Stretch or yoga session',
            'Active commute or stairs',
            'Dance, sport, or active play'
          ],
          progress: 0,
          daysCompleted: 0,
          daysRemaining: 21,
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(Date.now() + 21 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
          status: 'suggested',
          icon: Activity,
          colour: 'text-primary'
        };
      }

      if (challenge) {
        setSuggestedChallenge(challenge);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Challenge analysis error:', error);
      setIsLoading(false);
    }
  };

  const loadActiveChallenge = async () => {
    // Check localStorage for active challenge
    const storedChallenge = localStorage.getItem(`challenge_${userId}`);
    if (storedChallenge) {
      const challenge: Challenge = JSON.parse(storedChallenge);
      
      // Update progress
      const daysPassed = differenceInDays(new Date(), new Date(challenge.startDate));
      const daysRemaining = Math.max(0, challenge.duration - daysPassed);
      
      if (daysRemaining === 0) {
        challenge.status = 'completed';
      }
      
      challenge.daysCompleted = Math.min(daysPassed, challenge.duration);
      challenge.daysRemaining = daysRemaining;
      challenge.progress = (challenge.daysCompleted / challenge.duration) * 100;
      
      setActiveChallenge(challenge);
    }
  };

  const startChallenge = (challenge: Challenge) => {
    const startedChallenge = {
      ...challenge,
      status: 'active' as const,
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: format(new Date(Date.now() + challenge.duration * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')
    };
    
    localStorage.setItem(`challenge_${userId}`, JSON.stringify(startedChallenge));
    setActiveChallenge(startedChallenge);
    setSuggestedChallenge(null);
    
    toast({
      title: "Challenge Started! 🎯",
      description: `Your ${challenge.duration}-day journey begins now. Track daily to see your transformation!`
    });

    if (onStartChallenge) {
      onStartChallenge();
    }
  };

  const completeChallenge = () => {
    if (!activeChallenge) return;
    
    localStorage.removeItem(`challenge_${userId}`);
    setActiveChallenge(null);
    
    toast({
      title: "Challenge Completed! 🏆",
      description: "Amazing work! Check your progress to see how far you've come."
    });
  };

  const average = (arr: number[]): number => {
    return arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  };

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/2 mb-4" />
        <div className="h-32 bg-muted rounded" />
      </Card>
    );
  }

  // Show active challenge
  if (activeChallenge && activeChallenge.status === 'active') {
    const Icon = activeChallenge.icon;
    return (
      <Card className="p-6 bg-gradient-to-br from-primary/10 via-accent/5 to-background border-2 border-primary/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        
        <div className="relative space-y-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl bg-primary/10 ${activeChallenge.colour}`}>
                <Icon className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">{activeChallenge.title}</h3>
                <p className="text-muted-foreground">{activeChallenge.description}</p>
              </div>
            </div>
            <Badge className="bg-primary text-primary-foreground gap-1 px-3 py-1">
              <Flame className="w-3 h-3" />
              Day {activeChallenge.daysCompleted}/{activeChallenge.duration}
            </Badge>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Challenge Progress</span>
              <span className="text-muted-foreground">{activeChallenge.daysRemaining} days remaining</span>
            </div>
            <Progress value={activeChallenge.progress} className="h-3" />
          </div>

          {/* Goal */}
          <div className="p-4 bg-background/80 rounded-lg border border-border/50">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Your Goal</span>
            </div>
            <p className="text-sm">{activeChallenge.goal}</p>
          </div>

          {/* Expected Results */}
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-accent" />
              <span className="font-semibold text-sm">Expected Results</span>
            </div>
            <p className="text-sm text-muted-foreground">{activeChallenge.expectedImprovement}</p>
          </div>

          {/* Daily Actions */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Daily Actions
            </h4>
            <ul className="space-y-2">
              {activeChallenge.dailyActions.map((action, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  {action}
                </li>
              ))}
            </ul>
          </div>

          {activeChallenge.daysRemaining === 0 && (
            <Button 
              onClick={completeChallenge}
              className="w-full gap-2"
              size="lg"
            >
              <Trophy className="w-5 h-5" />
              Complete Challenge & See Results
            </Button>
          )}
        </div>
      </Card>
    );
  }

  // Show suggested challenge
  if (suggestedChallenge) {
    const Icon = suggestedChallenge.icon;
    return (
      <Card className="p-6 bg-gradient-to-br from-accent/5 via-background to-primary/5 border-accent/20">
        <div className="space-y-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl bg-accent/10 ${suggestedChallenge.colour}`}>
                <Icon className="w-8 h-8" />
              </div>
              <div>
                <Badge className="mb-2 bg-accent/20 text-accent border-accent/30">
                  Personalised for You
                </Badge>
                <h3 className="text-2xl font-bold">{suggestedChallenge.title}</h3>
                <p className="text-muted-foreground">{suggestedChallenge.description}</p>
              </div>
            </div>
          </div>

          {/* Current vs Target */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-background/50 rounded-lg border border-border/50">
              <div className="text-xs text-muted-foreground mb-1">Current Average</div>
              <div className="text-2xl font-bold">{suggestedChallenge.currentValue.toFixed(1)}</div>
              <div className="text-xs text-muted-foreground">{suggestedChallenge.targetMetric}</div>
            </div>
            <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
              <div className="text-xs text-muted-foreground mb-1">Target</div>
              <div className="text-2xl font-bold text-primary">{suggestedChallenge.targetValue}</div>
              <div className="text-xs text-muted-foreground">{suggestedChallenge.targetMetric}</div>
            </div>
          </div>

          {/* Expected Improvements */}
          <div className="p-4 bg-accent/5 rounded-lg border border-accent/20">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-accent" />
              <span className="font-semibold text-sm">What to Expect</span>
            </div>
            <p className="text-sm text-foreground mb-3">{suggestedChallenge.expectedImprovement}</p>
            
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                {suggestedChallenge.scienceRationale}
              </p>
            </div>
          </div>

          {/* Action Preview */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Your Daily Actions
            </h4>
            <div className="space-y-2">
              {suggestedChallenge.dailyActions.slice(0, 3).map((action, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-primary">{idx + 1}</span>
                  </div>
                  {action}
                </div>
              ))}
              <div className="text-xs text-muted-foreground ml-7">
                +{suggestedChallenge.dailyActions.length - 3} more actions
              </div>
            </div>
          </div>

          <Button 
            onClick={() => startChallenge(suggestedChallenge)}
            className="w-full gap-2"
            size="lg"
          >
            <Calendar className="w-5 h-5" />
            Start {suggestedChallenge.duration}-Day Challenge
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-8 text-center">
      <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
      <p className="text-muted-foreground">
        Keep tracking consistently to unlock personalised challenges
      </p>
    </Card>
  );
}
